// src/components/table/TripDrawer.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Paper,
  IconButton,
  useTheme,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { TaskInfo, SortOption, TouchPoint, TouchPointHoverPreview, ViewportOcclusionRect } from '../../models/types';
import TripTable from './TripTable';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';

export interface TripDrawerProps {
  taskInfo: TaskInfo | null;
  selectedTrips: string[];
  visibleTrips: string[];
  searchTerm: string;
  onTripSelect: (tripName: string) => void;
  onTripVisibility: (tripName: string) => void;
  onSearch: (term: string) => void;
  sortConfig: { key: SortOption; direction: 'asc' | 'desc' };
  onSortKeyChange: (key: SortOption) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
  onFocusLngLat?: (lngLat: [number, number]) => void;
  onFocusTrip?: (tripName: string) => void;
  onFocusTouchPoint?: (tp: TouchPoint) => void;
  onHoverTouchPoint?: (preview: TouchPointHoverPreview | null) => void;
  editMode?: boolean;
  editSourceTrip?: string | null;
  selectedEditTouchPointIds?: string[];
  onToggleEditTouchPoint?: (touchPoint: TouchPoint) => void;
  showTableTimelines?: boolean;
  onToggleTableTimelines?: (enabled: boolean) => void;
  onOpenTimeline?: (tripName: string) => void;
  onTimelineOrderChange?: (tripNames: string[]) => void;
  highlightedTripName?: string | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onStartTripEdit?: (tripName: string) => void;
  onCancelTripEdit?: () => void;
  onOcclusionRectsChange?: (rects: ViewportOcclusionRect[]) => void;
}

const TripDrawer: React.FC<TripDrawerProps> = (props) => {
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const [internalOpen, setInternalOpen] = useState(true);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const toggleControlRef = useRef<HTMLElement | null>(null);
  const { onOcclusionRectsChange } = props;
  const open = props.open ?? internalOpen;

  const readOcclusionRect = (node: Element | null): ViewportOcclusionRect | null => {
    if (!node) {
      return null;
    }
    const rect = node.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    return {
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
  };

  const publishOcclusionRects = useCallback(() => {
    if (!onOcclusionRectsChange) {
      return;
    }

    const rects = [
      open ? readOcclusionRect(drawerRef.current) : null,
      readOcclusionRect(toggleControlRef.current),
    ].filter((rect): rect is ViewportOcclusionRect => Boolean(rect));

    onOcclusionRectsChange(rects);
  }, [onOcclusionRectsChange, open]);

  useEffect(() => {
    if (!onOcclusionRectsChange) {
      return undefined;
    }

    publishOcclusionRects();

    const observer = new ResizeObserver(() => publishOcclusionRects());
    if (drawerRef.current) {
      observer.observe(drawerRef.current);
    }
    if (toggleControlRef.current) {
      observer.observe(toggleControlRef.current);
    }

    window.addEventListener('resize', publishOcclusionRects);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publishOcclusionRects);
      onOcclusionRectsChange([]);
    };
  }, [onOcclusionRectsChange, publishOcclusionRects]);

  const handleDrawerToggle = () => {
    const nextOpen = !open;
    if (props.open === undefined) {
      setInternalOpen(nextOpen);
    }
    props.onOpenChange?.(nextOpen);
  };

  const allVisible = props.taskInfo ? props.visibleTrips.length === props.taskInfo.trips.length : true;

  const handleToggleAllVisibility = (showAll: boolean) => {
    if (!props.taskInfo) return;
    const tripNames = props.taskInfo.trips.map(t => t.name);
    if (showAll) {
      // Turn on visibility for all trips that are currently hidden
      const hidden = tripNames.filter(name => !props.visibleTrips.includes(name));
      hidden.forEach(name => props.onTripVisibility(name));
    } else {
      // Turn off visibility for all trips that are currently visible
      const visible = tripNames.filter(name => props.visibleTrips.includes(name));
      visible.forEach(name => props.onTripVisibility(name));
    }
  };

  const tripTableProps = {
    ...props,
    allVisible,
    onToggleAllVisibility: handleToggleAllVisibility,
    onFocusLngLat: props.onFocusLngLat,
    onFocusTrip: props.onFocusTrip,
    onFocusTouchPoint: props.onFocusTouchPoint,
    onHoverTouchPoint: props.onHoverTouchPoint,
    editMode: props.editMode,
    editSourceTrip: props.editSourceTrip,
    selectedEditTouchPointIds: props.selectedEditTouchPointIds,
    onToggleEditTouchPoint: props.onToggleEditTouchPoint,
    showTableTimelines: props.showTableTimelines,
    onToggleTableTimelines: props.onToggleTableTimelines,
    onOpenTimeline: props.onOpenTimeline,
    onTimelineOrderChange: props.onTimelineOrderChange,
    highlightedTripName: props.highlightedTripName,
  };

  return (
    <>
      {/* Trip Table Drawer */}
      <Paper
        ref={drawerRef}
        elevation={0}
        sx={{
          width: open ? 408 : 0,
          height: '100%',
          overflow: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          borderRight: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {open && (
          <Box sx={{ flexGrow: 1, overflow: 'auto', height: '100%' }}>
            <TripTable {...tripTableProps} />
          </Box>
        )}
      </Paper>

      {/* Collapse button - sibling so it can stay above the timeline overlay */}
      {open && (
        <Paper
          ref={(node) => { toggleControlRef.current = node; }}
          elevation={3}
          sx={{
            position: 'absolute',
            top: 10,
            left: 358,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(50, 50, 50, 0.85)' : 'rgba(255, 255, 255, 0.9)',
            color: theme => theme.palette.mode === 'dark' ? 'grey.300' : 'grey.700',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(70, 70, 70, 0.9)' : 'rgba(245, 245, 245, 0.95)',
              color: theme => theme.palette.mode === 'dark' ? 'common.white' : 'grey.900',
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
            },
          }}
          onClick={handleDrawerToggle}
        >
          <KeyboardArrowLeftIcon sx={{ fontSize: '28px' }} />
        </Paper>
      )}
      
      {/* Standalone drawer toggle button - visible when drawer is closed */}
      {!open && (
        <IconButton
          ref={(node) => { toggleControlRef.current = node; }}
          aria-label="Open drawer"
          onClick={handleDrawerToggle}
          sx={{
            position: 'absolute',
            left: 10,
            top: 10,
            width: 40,
            height: 40,
            backgroundColor: mode === 'dark' ? 'rgba(66, 66, 66, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 100,
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(80, 80, 80, 0.9)' : 'rgba(240, 240, 240, 0.9)',
            },
          }}
        >
          <KeyboardArrowRightIcon sx={{ fontSize: '28px' }} />
        </IconButton>
      )}
    </>
  );
};

export default TripDrawer;
